import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ReduxSelectField, SwitchField } from 'common/components';

import { registerSubmitInterceptor } from '../submitInterceptors';
import { staticStyles } from './style';

const DEFAULT_VALUE = { enabled: false, ib_codes: [] };

const TagAutomationField = ({ input, meta, schema, disabled }) => {
  const value = input.value || DEFAULT_VALUE;
  const enabled = !!value.enabled;
  const ibCodes = Array.isArray(value.ib_codes) ? value.ib_codes : [];

  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmResolveRef = useRef(null);
  const pendingPayloadRef = useRef(null);

  const updateValue = patch => input.onChange({ ...DEFAULT_VALUE, ...value, ...patch });

  // Make sure the automation object always exists in the form payload (Create page)
  useEffect(() => {
    if (input.value === undefined) {
      input.onChange({ ...DEFAULT_VALUE });
    }
  }, []); // eslint-disable-line

  // Debounced preview counters for the selected IB codes
  const codesKey = ibCodes.join(',');
  useEffect(() => {
    if (!enabled) {
      setPreview(null);
      return undefined;
    }
    if (!codesKey) {
      setPreview({ n: 0, m: 0 });
      return undefined;
    }
    const query = codesKey
      .split(',')
      .map(id => `accounts=${id}`)
      .join('&');
    const timer = setTimeout(() => {
      axios
        .get(`/common/tag_automation_preview/?${query}`)
        .then(({ data }) => setPreview(data))
        .catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [enabled, codesKey]);

  // Intercept submission when the switch was turned OFF for an existing rule (Edit page)
  useEffect(() => {
    const unregister = registerSubmitInterceptor((payload, initialValues) => {
      const automation = payload && payload.automation;
      const wasEnabled = !!(initialValues && initialValues.automation && initialValues.automation.enabled);
      if (wasEnabled && automation && automation.enabled === false) {
        return new Promise(resolve => {
          pendingPayloadRef.current = payload;
          confirmResolveRef.current = resolve;
          setConfirmOpen(true);
        });
      }
      return payload;
    });
    return unregister;
  }, []);

  const handleConfirm = removeTags => {
    const payload = pendingPayloadRef.current;
    if (payload && payload.automation) {
      payload.automation = { ...payload.automation, remove_tags_on_disable: removeTags };
    }
    const resolve = confirmResolveRef.current;
    pendingPayloadRef.current = null;
    confirmResolveRef.current = null;
    setConfirmOpen(false);
    if (resolve) {
      resolve(payload);
    }
  };

  const options = Array.isArray(schema.options) ? schema.options : [];
  const previewCount = preview ? preview.n : ibCodes.length;
  const customersCount = preview ? preview.m : 0;

  return (
    <div className="TagAutomationField">
      <div className="TagAutomationField__header">
        <span className="TagAutomationField__label">{schema.title}</span>
        <SwitchField
          input={{
            name: `${input.name}.enabled`,
            value: enabled,
            // Turning the rule off discards the IB code selection so re-enabling starts empty
            onChange: () => updateValue(enabled ? { enabled: false, ib_codes: [] } : { enabled: true }),
          }}
          meta={{ touched: false, error: null }}
          disabled={disabled}
        />
      </div>

      {enabled && (
        <div className="TagAutomationField__content">
          <div className="TagAutomationField__subtitle">
            <FormattedMessage id="tagAutomationIbCodes" defaultMessage="IB codes" />
          </div>
          {schema.rule_text && <p className="TagAutomationField__rule-text">{schema.rule_text}</p>}
          <ReduxSelectField
            input={{
              name: `${input.name}.ib_codes`,
              value: ibCodes,
              onChange: codes => updateValue({ ib_codes: Array.isArray(codes) ? codes : [] }),
              onBlur: () => {},
            }}
            meta={{ touched: !!meta.touched, error: meta.error }}
            options={options}
            multi
            noLabel
            placeholder={schema.placeholder}
            disabled={disabled}
          />
          <div className="TagAutomationField__preview">
            {ibCodes.length ? (
              <FormattedMessage
                id="tagAutomationPreview"
                defaultMessage="{n} IB(s) selected · about {m} existing customers will be tagged on save."
                values={{ n: previewCount, m: customersCount }}
              />
            ) : (
              <FormattedMessage id="tagAutomationPreviewNone" defaultMessage="No IB codes selected." />
            )}
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="TagAutomationField__overlay">
          <div className="TagAutomationField__confirm">
            <div className="TagAutomationField__confirm-title">
              <FormattedMessage id="tagAutomationConfirmTitle" defaultMessage="Confirm Rule" />
            </div>
            <p className="TagAutomationField__confirm-text">
              <FormattedMessage
                id="tagAutomationConfirmText"
                defaultMessage="You are about to turn off this rule switch. Do you want to remove the corresponding tag from clients who already have this tag?"
              />
            </p>
            <div className="TagAutomationField__confirm-buttons">
              <button type="button" className="TagAutomationField__confirm-remove" onClick={() => handleConfirm(true)}>
                <FormattedMessage id="tagAutomationConfirmRemove" defaultMessage="Remove" />
              </button>
              <button type="button" className="TagAutomationField__confirm-cancel" onClick={() => handleConfirm(false)}>
                <FormattedMessage id="justCancel" defaultMessage="Cancel" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{staticStyles}</style>
    </div>
  );
};

TagAutomationField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

TagAutomationField.defaultProps = {
  disabled: false,
};

export default TagAutomationField;
